/*
 * Assign flow — V1.9.1 (design handoff README §The flows → A. Assign a place;
 * App Shell.dc.html is the measured reference). Presentation + client state
 * per the design; DATA LOGIC PRESERVED: my-api remains the allocation
 * authority (self-holder bootstrap / email invite / revoke-on-replace), and
 * NOTHING is sent before the explicit confirm.
 *
 * SHARED FLOW CHROME: header (back chevron · event name · `Step N of M` ·
 * 5px progress) · scrolling body · footer. Flow surface #12181a
 * (--tru-surface-1). Single-choice steps advance ON TAP with Back + a quiet
 * "Tap an option to continue" (no Continue button); field steps keep
 * Back + Continue with the blocked-reason bar DIRECTLY above the footer,
 * using the footer's own horizontal padding (16px mobile / 26px desktop).
 *
 * ROUTE: /orders/:orderRef/tickets/:ticketId/seats/:seatNo/assign — wizard
 * sub-steps are in-flow state, not routed.
 *
 * ONE-STEP PARENT SEND (BOARDROOM-2026-09-05-120305 item 5 + refinement
 * 120713): the guestparent routes have NO review step — the parent-email
 * entry page's `Confirm & send invite` CTA fires the send (stepList drops
 * 'confirm' on those routes only; the review surface stays for me/child/
 * other). A REPLACE shows the concrete-names banner at the top of that page;
 * the done screen carries a `Wrong address? Change & resend` escape whose
 * re-send rides the reassign revoke-then-rotate seam below (the prior invite
 * link is invalidated server-side — that is the safety story that replaced
 * the review page).
 *
 * DRAFT SCOPING: the draft is reset AND stamped with the seat
 * (`{orderRef}|{ticketIndex}.{seatIndex}`); route changes clear the previous
 * route's answers (assignModel.pick* patches).
 *
 * PERSISTENCE SEAM: routes that end in an email invite (someone-else-adult;
 * guestparent parent-email) submit through the EXISTING my-api allocation
 * call. Child / guestchild (incl. the teen my-route, mode 'child' + minor)
 * submit through the my-api CHILD-HOLDER contract (launch-gate slice) — the
 * client store now carries only the DISPLAY overlay (picker pools, Orders
 * row sub-lines), no longer the sole record. Teen view-only login issuance
 * remains client-recorded only (// TODO WIRE).
 */
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import type { CSSProperties } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { loadSession } from '../../auth/session'
import { getProfile, updateProfile } from '../../auth/cognitoProfile'
import { normalizeEmail } from '../../auth/normalizeEmail'
import { LiveDataNotice, useData } from '../../data/DataProvider'
import {
  allocateTicket, createChildHolder, ensureSelfHolder, myApiBase, revokeAllocation, MyApiError,
} from '../../data/myApi'
import { useShellChrome } from '../../components/Shell'
import { translate, useLocale, type TranslateFn } from '../../i18n/useLocale'
import {
  blockText, bracketChoices, buildOutcome, confirmLabel, doneHeadingWithFlags,
  doneSub, emptyDraft, emailOk, eyebrow, genderEligible, heading, intro, isChoiceStep,
  kidAgeVerdict, MONTHS, genderOptions, nextNote, permissionChoices, pickBracket, pickMode, pickableYears,
  pickPermission, pickRelation, pickerPool, relationChoices, resolveTicketPolicy, routeFlags,
  stepList, stepOk, summaryRows, teenCardVisible, teenName, ticketGenderRestriction, whoChoices, wireGender,
  DEFAULT_CHILD_FIELD_CONFIG, ageOnRaceFromIso, pickSelfBracket, selfAgeChoices, selfTeenYears,
  type AssignCtx, type AssignDraft, type AssignOutcome, type ChildFieldConfig, type SendFlags,
  type TeenSelfOpts, type TicketPolicy,
} from './assignModel'
import { teenSelfHolderEnabled } from '../../lib/teenSelfHolderFlag'
import {
  addChild, clearLocalAssignment, getAssignStoreSnapshot, listChildren, recordLocalAssignment,
  subscribeAssignStore,
} from './assignStore'
import { dobIso } from '../wizard/wizardModel'
import { seatKeyFor } from './ordersView'
import { holderLabel } from './ordersModel'
import { useOrderSeats } from './useOrderSeats'
import { bumpReadinessRefresh } from '../../data/getReady'
import { isPastEvent, raceDayFrom } from '../../data/pastEvents'
import { useGetReadyLiveOptional } from '../GetReady'

// ---------------------------------------------------------------------------
// Styles — V1.9.1 measured (App Shell.dc.html flow chrome + option cards)
// ---------------------------------------------------------------------------

const h1Style: CSSProperties = { fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }
const introStyle: CSSProperties = { fontSize: 13, color: 'var(--tru-text-secondary)', lineHeight: 1.5, margin: '6px 0 18px' }
const eyebrowStyle: CSSProperties = {
  fontFamily: 'var(--tru-font-mono)', fontSize: 9.5, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--tru-text-muted)', marginBlockEnd: 4,
}
const lblStyle: CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--tru-text-secondary)', display: 'block', marginBlockEnd: 6 }
const optionalStyle: CSSProperties = { color: 'var(--tru-text-muted)', fontWeight: 600 }
const inputStyle = (tone: 'idle' | 'ok' | 'bad' = 'idle'): CSSProperties => ({
  width: '100%', minHeight: 48, borderRadius: 11, padding: '0 13px', fontSize: 15,
  background: 'var(--tru-surface-1)', color: 'var(--tru-text-primary)', outline: 'none', fontFamily: 'inherit',
  boxSizing: 'border-box',
  border: `1px solid ${tone === 'idle' ? 'var(--tru-border-14)' : tone === 'ok' ? 'rgba(18,196,172,.55)' : 'var(--tru-status-error-border-60)'}`,
})
const hintStyle = (tone: 'idle' | 'ok' | 'bad'): CSSProperties => ({
  fontSize: 11.5, marginBlockStart: 7,
  ...(tone === 'idle'
    ? { color: 'var(--tru-text-quaternary)' }
    : tone === 'ok'
      ? { color: 'var(--tru-status-done-text)', fontWeight: 700 }
      : { color: 'var(--tru-status-error)', fontWeight: 700 }),
})
const selStyle: CSSProperties = {
  width: '100%', minWidth: 0, minHeight: 48, border: '1px solid var(--tru-border-14)', borderRadius: 11,
  padding: '0 8px', fontSize: 14, background: 'var(--tru-surface-1)', color: 'var(--tru-text-primary)', outline: 'none',
  fontFamily: 'inherit', appearance: 'none',
}
/** Option card (13/14px radius per measured markup) with the teal ring when picked. */
const cardBtnStyle = (on: boolean, radius: 13 | 14 = 13): CSSProperties => ({
  display: 'block', width: '100%', textAlign: 'start', fontFamily: 'inherit', cursor: 'pointer',
  padding: radius === 14 ? '14px 15px' : '13px 14px', marginBlockEnd: 10, borderRadius: radius,
  background: 'var(--tru-surface-1)',
  color: 'var(--tru-text-primary)', border: `1px solid ${on ? 'rgba(18,196,172,.5)' : 'var(--tru-border-12)'}`,
})
const tagChipStyle = (on: boolean): CSSProperties => ({
  fontFamily: 'var(--tru-font-mono)', fontSize: 8.5, letterSpacing: '.09em', borderRadius: 4, padding: '2px 5px',
  ...(on
    ? { color: 'var(--tru-status-done-text)', border: '1px solid rgba(18,196,172,.4)' }
    : { color: 'var(--tru-text-quaternary)', border: '1px solid var(--tru-border-14)' }),
})
const choiceTitleStyle = (on: boolean): CSSProperties => ({
  display: 'block', fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em',
  color: on ? 'var(--tru-text-primary)' : '#c3c9c5',
})
const backBtnStyle: CSSProperties = {
  flex: 'none', border: '1px solid var(--tru-border-14)', background: 'transparent',
  color: '#c3c9c5', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
  cursor: 'pointer', minHeight: 48, borderRadius: 12, padding: '0 16px',
}
const ctaStyle = (ok: boolean): CSSProperties => ({
  flex: 1, border: 0, borderRadius: 12, minHeight: 48, background: 'var(--tru-status-teal)',
  color: 'var(--tru-accent-ink)', fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
  ...(ok ? { cursor: 'pointer' } : { opacity: 0.35, cursor: 'not-allowed' }),
})
const verdictStyle = (tone: 'bad' | 'ok'): CSSProperties => ({
  fontSize: 11.5, lineHeight: 1.5, marginBlockStart: 10, padding: '10px 12px', borderRadius: 11, fontWeight: 700,
  ...(tone === 'bad'
    ? { color: 'var(--tru-status-error)', background: 'rgba(229,83,61,.09)', border: '1px solid rgba(229,83,61,.32)' }
    : { color: 'var(--tru-status-done-text)', background: 'rgba(18,196,172,.08)', border: '1px solid rgba(18,196,172,.28)' }),
})
const summaryCardStyle: CSSProperties = {
  background: 'var(--tru-surface-3)', border: '1px solid var(--tru-hairline-08)',
  borderRadius: 14, padding: '4px 15px',
}
const summaryRowStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0',
  borderBlockEnd: '1px solid var(--tru-hairline-06)',
}

// ---------------------------------------------------------------------------
// Flow component (presentational + in-flow step state; tests drive it directly)
// ---------------------------------------------------------------------------

export function AssignFlow({
  ctx, seatKey, policy, raceDay, config = DEFAULT_CHILD_FIELD_CONFIG,
  sameEventEmails, sameUnitEmails,
  onSubmit, onExit, onDone, onGetReady, teenSelf,
}: {
  ctx: AssignCtx
  /** `{orderRef}|{ticketIndex}.{seatIndex}` — stamps the draft (scoping rule). */
  seatKey: string
  policy: TicketPolicy
  /** Race day for age-on-race-day band validation. */
  raceDay: Date
  /** Event-owner child-field configuration (// TODO WIRE: config source —
   *  defaults DOB-required, gender-optional). */
  config?: ChildFieldConfig
  /** NON-BLOCKING cross-unit heads-up (CMT-2026-08-29-026): normalized emails
   *  already holding a place on ANOTHER unit of this event within the loaded
   *  order data. Entering one shows "enough time to attend both" — it never
   *  gates the step (cross-unit repeats are ALLOWED; the same-UNIT duplicate
   *  is the server's 422 hard block, surfaced on the email field). */
  sameEventEmails?: ReadonlySet<string>
  /** Same-UNIT sibling LIVE-holder emails (normalized) — the complement of
   *  `sameEventEmails` (adversarial-review P1). A REPLACE submit pre-checks
   *  the target address against this set BEFORE calling the submit seam,
   *  because a replace is two non-atomic server calls (revoke THEN allocate):
   *  without the pre-check, replacing a holder with their sibling's email
   *  committed the revoke and then 422'd, leaving the seat empty with its QR
   *  killed. On a match the duplicate field error shows with ZERO network
   *  calls; the server 422 remains the authoritative backstop (this loaded
   *  set can be stale). Fresh (non-replace) submits are NOT pre-blocked —
   *  their server 422 is non-destructive and authoritative. */
  sameUnitEmails?: ReadonlySet<string>
  /** Runs ONLY from the explicit confirm button. Rejection keeps the flow on
   *  confirm with an honest error. Resolved flags gate the done-screen email
   *  claims (honesty: never claim a send that did not happen). */
  onSubmit: (outcome: AssignOutcome) => Promise<Partial<SendFlags> | void>
  onExit: () => void
  onDone: () => void
  /** Optional "Get ready now" affordance on the DONE screen — self-assign only. */
  onGetReady?: () => void
  /** TEEN SELF-HOLDER (VITE_TEEN_SELF_HOLDER_ENABLED): the "Myself" age step.
   *  Absent / disabled ⇒ the flow is exactly as before. */
  teenSelf?: TeenSelfOpts
}) {
  const { isMobile } = useShellChrome()
  const { t, dir } = useLocale()
  const [draft, setDraft] = useState<AssignDraft>(() => emptyDraft(seatKey))
  const [fs, setFs] = useState(0)
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'error'>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Field-level duplicate-participant reject (CMT-2026-08-29-026): the server
  // 422 lands ON the email input (details step), not as a confirm-step line.
  const [dupEmailError, setDupEmailError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<AssignOutcome | null>(null)
  const [sent, setSent] = useState<SendFlags>({ inviteEmailSent: true, teenLoginEmailSent: false })
  // Wrong-address escape (BOARDROOM-2026-09-05-120305 item 5): once a send
  // has SUCCEEDED this session, any re-send from the prefilled entry step is
  // a REPLACE — buildOutcome must carry reassign:true so the submit seam
  // rides the verified revoke-then-rotate mechanics (liveSubmit revokes the
  // prior invite first; the old link 409s server-side). ctx.holder alone
  // cannot know this: the container's order data is stale until onDone.
  const [resendArmed, setResendArmed] = useState(false)
  // The JUST-SENT recipient (review fix F1b): snapshotted from the draft at
  // the moment the wrong-address escape arms, BEFORE the user edits anything
  // — the armed pass's replace banner must name the person the invite
  // actually went to, never the stale ctx.holder (the container's order data
  // is not refreshed until onDone, so once a send has succeeded this session
  // ctx.holder is history).
  const [resendSnapshot, setResendSnapshot] = useState<string | null>(null)

  // Children live in the client store (two pools — // TODO WIRE server contract).
  const store = useSyncExternalStore(subscribeAssignStore, getAssignStoreSnapshot, getAssignStoreSnapshot)

  // Draft-scoping: answers only count for the seat they were given on.
  if (draft.seatKey !== seatKey) {
    setDraft(emptyDraft(seatKey))
    setFs(0)
    setSubmitState('idle')
    setSubmitError(null)
    setDupEmailError(null)
    setOutcome(null)
    setResendArmed(false)
    setResendSnapshot(null)
  }

  // Any details-step edit invalidates a standing duplicate-participant verdict
  // (review P1-2: on the child routes the reject renders over the picker/add
  // form, whose fields all flow through this setter; the email input's own
  // explicit clear is kept — same effect).
  const set = (patch: Partial<AssignDraft>) => {
    setDupEmailError(null)
    setDraft((d) => ({ ...d, ...patch }))
  }

  const flags = routeFlags(draft)
  const pool = useMemo(
    () => pickerPool(store.children, flags, raceDay, t),
    [store.children, flags.routeGuest, raceDay, t], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const steps = stepList(draft, policy, teenSelf)
  const total = steps.length
  const done = fs >= total
  const cur = steps[Math.min(fs, total - 1)]
  const adding = draft.addKid || pool.length === 0
  const ok = stepOk(cur, draft, policy, config, pool, raceDay, ctx.ticketType)
  const choice = isChoiceStep(cur)
  const showBlock = !done && !choice && !ok
  const isLast = !done && fs === total - 1
  const teenCard = !done && cur === 'details' && teenCardVisible(draft, flags, pool, adding)
  const chosenTeen = teenName(draft, pool, adding)

  // Self-assign gender guard (CMT-2026-08-11-010806) — data logic preserved.
  const selfGender = draft.mode === 'me'
    ? genderEligible(ctx.myGender, ctx.ticketType)
    : { ok: true as const, reason: undefined }
  const genderBlocked = !selfGender.ok

  const back = () => {
    if (fs === 0) onExit()
    else setFs((n) => n - 1)
  }
  const goNext = () => { if (ok) setFs((n) => n + 1) }
  /** Single-choice steps commit ON TAP and advance (QA 10.1). */
  const tapChoice = (patch: Partial<AssignDraft>) => {
    setDraft((d) => ({ ...d, ...patch }))
    setFs((n) => n + 1)
  }
  /** Land a duplicate-participant reject where the mistake lives: ON the
   *  details step when the route has one — the email input on the invite
   *  routes, the alert box over the child picker/add form on the pickChild
   *  routes (review P1-2: dupEmailError previously only ever RENDERED in the
   *  email block, so a child-route duplicate stranded the user silently) —
   *  and the confirm error line otherwise (self-assign). Shared by the
   *  client-side replace pre-checks and the server-422 catch below. */
  const landDuplicateError = (msg: string) => {
    const di = steps.indexOf('details')
    if (di >= 0) {
      setDupEmailError(msg)
      setSubmitState('error')
      setSubmitError(null)
      setFs(di)
      return
    }
    setSubmitState('error')
    setSubmitError(msg)
  }
  const submit = () => {
    if (!ok || submitState === 'submitting') return
    if (genderBlocked) {
      setSubmitState('error')
      setSubmitError(selfGender.reason ?? t('assign.error.gender'))
      return
    }
    // reassign: a standing holder (replace) OR a change-&-resend after this
    // session's own successful send (resendArmed) — both must revoke first.
    const o = buildOutcome(draft, ctx, pool, !!ctx.holder || resendArmed)
    // REPLACE pre-check, EMAIL axis (adversarial-review P1): the reassign
    // seam fires revoke BEFORE allocate, so a same-unit duplicate 422 from
    // the server would arrive only AFTER the standing holder was destroyed.
    // Check the target address against the LIVE same-unit sibling holders
    // client-side first — a match surfaces the field error with NOTHING
    // sent. Only the email routes carry an address here (o.email is '' on
    // the child routes — since the child-holder slice those DO revoke on
    // reassign too, and their identity pre-check is the CHILD axis inside
    // liveSubmit: childDuplicateOnUnit, review P1-3); self-assign targets
    // the account holder's own address.
    const replaceTarget = o.mode === 'me' ? ctx.myEmail : o.email
    if (o.reassign && replaceTarget && sameUnitEmails?.has(normalizeEmail(replaceTarget))) {
      landDuplicateError(t('assign.error.duplicateParticipant'))
      return
    }
    setSubmitState('submitting')
    setSubmitError(null)
    onSubmit(o)
      .then((res) => {
        setSent({
          inviteEmailSent: res && typeof res.inviteEmailSent === 'boolean' ? res.inviteEmailSent : true,
          teenLoginEmailSent: res && typeof res.teenLoginEmailSent === 'boolean' ? res.teenLoginEmailSent : false,
        })
        setOutcome(o)
        setSubmitState('idle')
        setFs(total) // ASSIGNED
      })
      .catch((e) => {
        // Duplicate participant in ONE unit (CMT-2026-08-29-026): a 422 with
        // reason 'duplicate_participant' belongs ON the email field — return
        // to the details step with the input marked, so the fix (a different
        // address) happens where the mistake lives. Routes without a details
        // step (self-assign) keep the confirm-step error line.
        if (e instanceof MyApiError && e.reason === 'duplicate_participant') {
          landDuplicateError(e.serverMessage ?? t('assign.error.duplicateParticipant'))
          return
        }
        setSubmitState('error')
        setSubmitError(e instanceof Error ? e.message : t('assign.error.failed'))
      })
  }

  /** Wrong-address escape (done screen, guestparent one-step send): return to
   *  the PREFILLED entry step and ARM replace semantics for the re-send —
   *  the next submit revokes the just-sent invite before allocating the
   *  corrected address (revoke-then-rotate; reuses the liveSubmit reassign
   *  seam, never a duplicated revoke call). The draft is untouched, so every
   *  field comes back exactly as sent. */
  const changeResend = () => {
    // Snapshot the JUST-SENT recipient off the untouched draft BEFORE the
    // user edits it (F1b). The escape is guestparent-only (done-screen gate),
    // so the invite's recipient is the PARENT: their name when given, else
    // the address the invite actually went to — never the child (the child
    // is unchanged by a wrong-address correction, and never the recipient).
    setResendSnapshot(draft.parentName.trim() || draft.email.trim() || null)
    setResendArmed(true)
    setOutcome(null)
    setSubmitState('idle')
    setSubmitError(null)
    setFs(Math.max(steps.indexOf('details'), 0))
  }

  // Flow frame — cancels the shell content padding (same as wizard/waiver).
  const pad = isMobile
    ? { margin: '-18px -16px -26px', height: 'calc(100% + 44px)' }
    : { margin: '-26px -30px -36px', height: 'calc(100% + 62px)' }
  const headPad = isMobile ? '12px 16px 10px' : '18px 26px 12px'
  const bodyPad = isMobile ? '16px 16px 20px' : '20px 26px 24px'
  const footPad = isMobile ? '10px 16px 12px' : '14px 26px 16px'
  // Blocked bar uses the FOOTER's own horizontal padding (contract, QA 9.2).
  const blockPadX = isMobile ? 16 : 26
  // Back chevron encodes direction — swapped in code, never CSS-mirrored.
  const backChev = dir === 'rtl' ? '›' : '‹'

  const rows = summaryRows(draft, ctx, policy, pool, t, teenSelf)
  const parentNamed = draft.parentName.trim()
  const emailTone = dupEmailError ? 'bad' : !draft.email ? 'idle' : emailOk(draft.email) ? 'ok' : 'bad'
  // Cross-unit heads-up (never blocks, never shown over a hard reject).
  const crossWarn = !dupEmailError && emailOk(draft.email) && !!sameEventEmails?.has(normalizeEmail(draft.email))
  const teenTone = !draft.teenEmail ? 'idle' : emailOk(draft.teenEmail) ? 'ok' : 'bad'
  const days = Array.from({ length: 31 }, (_, i) => String(i + 1))
  // DOB years the picker OFFERS: derived from the REAL race day + the
  // ticket's effective window (band ∩ under-signing on the teen routes) —
  // never a hardcoded range (BOARDROOM-2026-09-01-202244 WU4). Cusp years
  // stay in the list; the exact-DOB gate (kidInBand → blockText) refuses the
  // out-of-window day/month combos. Day + month stay full.
  const years = pickableYears(policy, flags.teen, raceDay).map(String)
  // ADD-path gender requirement (quality P0 launch-blocker, 2026-09-02): on a
  // gender-RESTRICTED ticket (the exact-token assignModel parse) the add
  // form's gender field is REQUIRED and ALWAYS asked — the restriction
  // overrides an event-owner Optional/'Not asked' config (a never-rendered
  // field would otherwise deadlock the missing-gender gate). Unrestricted
  // tickets keep the configured optionality: the RESTRICTION, not the blank,
  // drives the requirement.
  const genderRestriction = ticketGenderRestriction(ctx.ticketType)
  const askGender = config.childGender !== 'Not asked' || genderRestriction !== null
  const genderRequired = config.childGender === 'Required' || genderRestriction !== null
  const kidVerdict = kidAgeVerdict(draft, policy, raceDay, ctx.ticketType, t)

  return (
    <div id="assign.root" style={{ ...pad, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--tru-surface-1)' }}>
      {/* HEAD — ‹ back · event name · Step N of M / ASSIGNED · 5px progress */}
      <div id="assign.head" style={{ flex: 'none', padding: headPad, borderBlockEnd: '1px solid var(--tru-hairline-07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <button
            onClick={onExit}
            aria-label={t('flow.close')}
            style={{ border: 0, background: 'transparent', color: 'var(--tru-text-tertiary)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: '0 4px' }}
          >{backChev}</button>
          <span data-testid="assign-context" style={{ fontSize: 12, color: 'var(--tru-text-tertiary)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {ctx.evName || ctx.ticketType}
          </span>
          <span data-testid="assign-stepnum" style={{ fontFamily: 'var(--tru-font-mono)', fontSize: 10, color: 'var(--tru-text-tertiary)', flex: 'none' }}>
            {done ? t('flow.assigned') : t('flow.step', { n: Math.min(fs + 1, total), m: total })}
          </span>
        </div>
        <div style={{ height: 'var(--tru-progress-h)', borderRadius: 'var(--tru-progress-radius)', background: 'var(--tru-progress-track)', overflow: 'hidden', marginBlockStart: 10 }}>
          <div data-testid="assign-progress" style={{ height: '100%', borderRadius: 'var(--tru-progress-radius)', background: 'var(--tru-progress-fill)', width: `${Math.min(((fs + 1) / total) * 100, 100)}%` }} />
        </div>
      </div>

      {/* BODY */}
      <div id="assign.body" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: bodyPad }}>
        {/* REPLACE banner — TOP of the one-step parent-send entry page
            (Duncan refinement BOARDROOM-2026-09-05-120713): the send fires on
            this step now, so the confirm reads HERE with CONCRETE names —
            the standing holder in the `current` slot, the new person from the
            live-typed child name / parent email as available ('…' until one
            is). Roles never render in this line. WHO IS CURRENT (review fix
            F1b): on the ARMED pass (resendArmed — a send succeeded THIS
            session) the current holder is the just-sent recipient snapshot,
            NEVER the stale ctx.holder from the container's unrefreshed order
            data; ctx.holder names current only on a plain (pre-send) replace. */}
        {!done && cur === 'details' && flags.guestParent && (ctx.holder || resendArmed) && (
          <div
            data-testid="assign-replace-banner"
            role="alert"
            style={{ display: 'flex', gap: 9, padding: '12px 13px', marginBlockEnd: 14, borderRadius: 12, background: 'rgba(230,193,90,.12)', border: '1px solid rgba(230,193,90,.4)' }}
          >
            <span aria-hidden style={{ flex: 'none', color: 'var(--tru-status-amber)', fontSize: 13 }}>⚠</span>
            <span style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--tru-status-amber)', fontWeight: 800, overflowWrap: 'anywhere' }}>
              {t('assign.replaceBanner', {
                current: (resendArmed ? resendSnapshot : ctx.holder) || '…',
                next: draft.name.trim() || draft.email.trim() || '…',
              })}
            </span>
          </div>
        )}
        {!done && (
          <>
            <div data-testid="assign-eyebrow" style={eyebrowStyle}>{eyebrow(cur, ctx, t)}</div>
            <h1 data-testid="assign-heading" style={h1Style}>{heading(cur, draft, ctx, adding, t)}</h1>
            {/* `Aged {min}–{max}` beneath the ticket name — who step only */}
            {cur === 'who' && policy.band && (
              <div data-testid="assign-band" style={{ fontSize: 13, color: 'var(--tru-text-secondary)', marginBlockStart: 4 }}>
                {t('assign.aged', { min: policy.band.min, max: policy.band.max })}
              </div>
            )}
            {intro(cur, draft, policy, t, teenSelf) !== '' && (
              <p data-testid="assign-intro" style={introStyle}>{intro(cur, draft, policy, t, teenSelf)}</p>
            )}
            {/* the question line — who step */}
            {cur === 'who' && (
              <div data-testid="assign-question" style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', margin: '16px 0 12px' }}>
                {ctx.holder ? t('assign.question.whoReassign') : t('assign.question.who')}
              </div>
            )}
          </>
        )}

        {/* re-assign warning — amber, exact measured copy */}
        {!done && ctx.holder && (
          <div data-testid="assign-reassign-note" style={{ display: 'flex', gap: 9, padding: '11px 13px', marginBlockEnd: 16, borderRadius: 12, background: 'rgba(230,193,90,.09)', border: '1px solid rgba(230,193,90,.3)' }}>
            <span aria-hidden style={{ flex: 'none', color: 'var(--tru-status-amber)', fontSize: 13 }}>⚠</span>
            <span style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--tru-status-amber)', fontWeight: 700 }}>
              {t('assign.reassignNote', { who: ctx.holder })}
            </span>
          </div>
        )}

        {/* ---- WHO (single-choice — advances on tap) ---- */}
        {!done && cur === 'who' && (
          <div id="assign.step.who">
            {whoChoices(policy, ctx.myName, t).map((c) => (
              <button
                key={c.mode}
                data-testid="assign-choice"
                data-mode={c.mode}
                aria-pressed={draft.mode === c.mode}
                onClick={() => tapChoice(pickMode(c.mode))}
                style={{
                  ...cardBtnStyle(draft.mode === c.mode),
                  display: 'grid', gridTemplateColumns: '26px minmax(0,1fr)', columnGap: 11, alignItems: 'center',
                }}
              >
                <span aria-hidden style={{
                  width: 26, height: 26, borderRadius: 8, display: 'grid', placeItems: 'center', fontSize: 12,
                  ...(draft.mode === c.mode
                    ? { background: 'var(--tru-status-teal)', color: 'var(--tru-accent-ink)', fontWeight: 800 }
                    : { background: 'rgba(255,255,255,.06)', color: 'var(--tru-text-tertiary)' }),
                }}>{c.icon}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>{c.label}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {/* ---- AGE BRACKET (single-choice; the bracket, never a DOB) ---- */}
        {!done && cur === 'bracket' && (
          <div id="assign.step.bracket">
            {bracketChoices(policy, t).map((o) => (
              <button
                key={o.k}
                data-testid="assign-bracket-opt"
                data-bracket={o.k}
                aria-pressed={draft.bracket === o.k}
                onClick={() => tapChoice(pickBracket(o.k))}
                style={cardBtnStyle(draft.bracket === o.k, 14)}
              >
                <span style={{ display: 'flex', alignItems: 'center', marginBlockEnd: 6 }}>
                  <span className="mono" style={tagChipStyle(draft.bracket === o.k)}>{o.tag}</span>
                </span>
                <span style={choiceTitleStyle(draft.bracket === o.k)}>{o.title}</span>
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--tru-text-tertiary)', marginBlockStart: 5, lineHeight: 1.5 }}>{o.note}</span>
              </button>
            ))}
          </div>
        )}

        {/* ---- SELF AGE (TEEN SELF-HOLDER, flag on only): 18+ advances on
            tap; Under 18 reveals the holder's own DOB, limited to years that
            can give 16–17 on race day (selfTeenYears ← effectiveWindow). ---- */}
        {!done && cur === 'selfAge' && (
          <div id="assign.step.selfAge">
            {selfAgeChoices(policy, t).map((o) => (
              <button
                key={o.k}
                data-testid="assign-selfage-opt"
                data-bracket={o.k}
                aria-pressed={draft.selfBracket === o.k}
                onClick={() => (o.k === 'adult' ? tapChoice(pickSelfBracket('adult')) : set(pickSelfBracket('minor')))}
                style={cardBtnStyle(draft.selfBracket === o.k, 14)}
              >
                <span style={choiceTitleStyle(draft.selfBracket === o.k)}>{o.title}</span>
                {o.note && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--tru-text-tertiary)', marginBlockStart: 5, lineHeight: 1.5 }}>{o.note}</span>}
              </button>
            ))}
            {draft.selfBracket === 'minor' && (
              <>
                <div style={{ height: 6 }} />
                <label style={lblStyle}>{t('assign.selfAge.dob')}</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', gap: 8 }}>
                  <select data-testid="assign-self-dob-day" aria-label={t('assign.field.dob.day')} value={draft.selfDobD || t('assign.field.dob.day')} onChange={(e) => set({ selfDobD: e.target.value === t('assign.field.dob.day') ? '' : e.target.value })} style={selStyle}>
                    {[t('assign.field.dob.day'), ...days].map((o) => <option key={o}>{o}</option>)}
                  </select>
                  <select data-testid="assign-self-dob-month" aria-label={t('assign.field.dob.month')} value={draft.selfDobM || t('assign.field.dob.month')} onChange={(e) => set({ selfDobM: e.target.value === t('assign.field.dob.month') ? '' : e.target.value })} style={selStyle}>
                    {[t('assign.field.dob.month'), ...MONTHS].map((o) => <option key={o}>{o}</option>)}
                  </select>
                  <select data-testid="assign-self-dob-year" aria-label={t('assign.field.dob.year')} value={draft.selfDobY || t('assign.field.dob.year')} onChange={(e) => set({ selfDobY: e.target.value === t('assign.field.dob.year') ? '' : e.target.value })} style={selStyle}>
                    {[t('assign.field.dob.year'), ...selfTeenYears(policy, raceDay).map(String)].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--tru-text-quaternary)', marginBlockStart: 7, lineHeight: 1.5 }}>{t('assign.selfAge.dob.hint')}</div>
              </>
            )}
          </div>
        )}

        {/* ---- WHOSE TEENAGER (plain A-or-B — no tags, no sub-notes) ---- */}
        {!done && cur === 'relation' && (
          <div id="assign.step.relation">
            {relationChoices(t).map((o) => (
              <button
                key={o.k}
                data-testid="assign-relation-opt"
                data-rel={o.k}
                aria-pressed={draft.rel === o.k}
                onClick={() => tapChoice(pickRelation(o.k))}
                style={cardBtnStyle(draft.rel === o.k, 14)}
              >
                <span style={choiceTitleStyle(draft.rel === o.k)}>{o.title}</span>
              </button>
            ))}
          </div>
        )}

        {/* ---- WHO SIGNS (single-choice) ---- */}
        {!done && cur === 'permission' && (
          <div id="assign.step.permission">
            {permissionChoices(t).map((o) => (
              <button
                key={o.k}
                data-testid="assign-perm-opt"
                data-perm={o.k}
                aria-pressed={draft.perm === o.k}
                onClick={() => tapChoice(pickPermission(o.k))}
                style={cardBtnStyle(draft.perm === o.k, 14)}
              >
                <span style={{ display: 'flex', alignItems: 'center', marginBlockEnd: 6 }}>
                  <span className="mono" style={tagChipStyle(draft.perm === o.k)}>{o.tag}</span>
                </span>
                <span style={choiceTitleStyle(draft.perm === o.k)}>{o.title}</span>
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--tru-text-tertiary)', marginBlockStart: 5, lineHeight: 1.5 }}>{o.note}</span>
              </button>
            ))}
          </div>
        )}

        {/* ---- DETAILS ---- */}
        {!done && cur === 'details' && (
          <div id="assign.step.details">
            {/* Duplicate-participant reject on the CHILD routes (review P1-2):
                there is no email input here, so the server's/pre-check's
                verdict renders as an alert over the picker/add form — never a
                silent strand. Cleared by any details edit (see `set`). */}
            {flags.pickChild && dupEmailError && (
              <div
                data-testid="assign-dup-error"
                role="alert"
                style={{ ...verdictStyle('bad'), marginBlockStart: 0, marginBlockEnd: 12 }}
              >
                {dupEmailError}
              </div>
            )}
            {/* Email routes: guestparent (parent name optional + parent email) /
                other-adult (their email + optional name). */}
            {!flags.pickChild && (
              <>
                {flags.guestParent && (
                  <>
                    <label style={lblStyle}>
                      {t('assign.field.parentName.label')}{' '}
                      <span style={optionalStyle}>{t('assign.field.optionalSuffix')}</span>
                    </label>
                    <input
                      data-testid="assign-parent-name"
                      value={draft.parentName}
                      onChange={(e) => set({ parentName: e.target.value })}
                                            style={inputStyle()}
                    />
                    {/* live `We'll write "Dear {name}"` hint */}
                    <div data-testid="assign-parent-hint" style={hintStyle(parentNamed ? 'ok' : 'idle')}>
                      {parentNamed
                        ? t('assign.field.parentName.hintNamed', { name: parentNamed })
                        : t('assign.field.parentName.hintIdle')}
                    </div>
                    <div style={{ height: 16 }} />
                  </>
                )}
                <label style={lblStyle}>
                  {flags.guestParent ? t('assign.field.parentEmail.label') : t('assign.field.email.label')}
                </label>
                <input
                  data-testid="assign-email"
                  value={draft.email}
                  onChange={(e) => { setDupEmailError(null); set({ email: e.target.value }) }}
                  placeholder={t('assign.field.email.placeholder')}
                  style={inputStyle(emailTone)}
                />
                <div data-testid="assign-email-hint" style={hintStyle(emailTone)}>
                  {dupEmailError
                    ? dupEmailError
                    : emailTone === 'idle'
                      ? t(flags.guestParent ? 'assign.field.parentEmail.hintIdle' : 'assign.field.email.hintIdle')
                      : emailTone === 'ok' ? t('assign.field.email.ok') : t('assign.field.email.bad')}
                </div>
                {/* NON-BLOCKING cross-unit heads-up (CMT-2026-08-29-026): the
                    address already holds a place on another ticket of this
                    event — allowed; just the time-clash nudge. */}
                {crossWarn && (
                  <div data-testid="assign-crossticket-warn" role="note" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBlockStart: 8, padding: '9px 12px', borderRadius: 11, background: 'rgba(230,193,90,.09)', border: '1px solid rgba(230,193,90,.3)' }}>
                    <span aria-hidden style={{ flex: 'none', color: 'var(--tru-status-amber)', fontSize: 12 }}>⚠</span>
                    <span style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--tru-status-amber)', fontWeight: 700 }}>
                      {t('assign.warn.sameEventElsewhere')}
                    </span>
                  </div>
                )}
                <div style={{ height: 16 }} />
                <label style={lblStyle}>
                  {flags.guestParent ? t('assign.field.childName.label') : t('assign.field.name.label')}{' '}
                  <span style={optionalStyle}>{t('assign.field.optionalSuffix')}</span>
                </label>
                <input
                  data-testid="assign-name"
                  value={draft.name}
                  onChange={(e) => set({ name: e.target.value })}
                  placeholder={t('assign.field.name.placeholder')}
                  style={inputStyle()}
                />
              </>
            )}

            {/* Child/teen picker — the pool matching the chosen route ONLY. */}
            {flags.pickChild && !adding && (
              <>
                {pool.map((k) => (
                  <button
                    key={k.key}
                    data-testid="assign-kid-card"
                    data-child={k.key}
                    aria-pressed={draft.child === k.key}
                    onClick={() => set({ child: k.key, name: k.name, addKid: false })}
                    style={cardBtnStyle(draft.child === k.key)}
                  >
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em' }}>{k.name}</span>
                    {k.note && (
                      <span style={{ display: 'block', fontSize: 11.5, color: 'var(--tru-text-tertiary)', marginBlockStart: 3 }}>{k.note}</span>
                    )}
                  </button>
                ))}
                <button
                  data-testid="assign-add-child"
                  onClick={() => set({ addKid: true, child: '' })}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', minHeight: 48, border: '1.5px dashed rgba(255,255,255,.18)', borderRadius: 13, background: 'transparent', color: 'var(--tru-text-primary)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', padding: '0 14px' }}
                >
                  <span aria-hidden style={{ flex: 'none', color: 'var(--tru-status-teal-bright)', fontSize: 16 }}>+</span>
                  <span>{t(flags.noun === 'teenager' ? 'assign.addChild.addTeenLabel' : 'assign.addChild.addChildLabel')}</span>
                </button>
              </>
            )}

            {/* Add-a-child form: names + DOB (day/month-NAME/year selects) +
                gender — requiredness is event-owner config. */}
            {flags.pickChild && adding && (
              <>
                <div data-testid="assign-add-hint" style={{ fontSize: 11.5, color: 'var(--tru-text-tertiary)', lineHeight: 1.5, marginBlockEnd: 14 }}>
                  {(pool.length === 0
                    ? `${t(
                        flags.routeGuest
                          ? flags.noun === 'teenager' ? 'assign.addChild.noneGuestTeen' : 'assign.addChild.noneGuestChild'
                          : 'assign.addChild.noneOwnChild',
                      )} `
                    : '') + t('assign.addChild.rest')}
                </div>
                <label style={lblStyle}>{t('assign.field.firstName')}</label>
                <input data-testid="assign-kid-first" value={draft.kidFirst} onChange={(e) => set({ kidFirst: e.target.value })} placeholder="Mia" style={inputStyle()} />
                <div style={{ height: 14 }} />
                <label style={lblStyle}>{t('assign.field.lastName')}</label>
                <input data-testid="assign-kid-last" value={draft.kidLast} onChange={(e) => set({ kidLast: e.target.value })} placeholder="Diaz" style={inputStyle()} />
                <div style={{ height: 16 }} />
                <label style={lblStyle}>
                  {t('assign.field.dob')}
                  {config.childDob !== 'Required' && <> <span style={optionalStyle}>{t('assign.field.optionalSuffix')}</span></>}
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', gap: 8 }}>
                  <select data-testid="assign-kid-dob-day" aria-label={t('assign.field.dob.day')} value={draft.kidDobD || t('assign.field.dob.day')} onChange={(e) => set({ kidDobD: e.target.value === t('assign.field.dob.day') ? '' : e.target.value })} style={selStyle}>
                    {[t('assign.field.dob.day'), ...days].map((o) => <option key={o}>{o}</option>)}
                  </select>
                  <select data-testid="assign-kid-dob-month" aria-label={t('assign.field.dob.month')} value={draft.kidDobM || t('assign.field.dob.month')} onChange={(e) => set({ kidDobM: e.target.value === t('assign.field.dob.month') ? '' : e.target.value })} style={selStyle}>
                    {[t('assign.field.dob.month'), ...MONTHS].map((o) => <option key={o}>{o}</option>)}
                  </select>
                  <select data-testid="assign-kid-dob-year" aria-label={t('assign.field.dob.year')} value={draft.kidDobY || t('assign.field.dob.year')} onChange={(e) => set({ kidDobY: e.target.value === t('assign.field.dob.year') ? '' : e.target.value })} style={selStyle}>
                    {[t('assign.field.dob.year'), ...years].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {kidVerdict && (
                  <div data-testid="assign-kid-age" data-tone={kidVerdict.tone} style={verdictStyle(kidVerdict.tone)}>{kidVerdict.text}</div>
                )}
                {askGender && (
                  <>
                    <div style={{ height: 16 }} />
                    <label style={lblStyle}>
                      {t('assign.field.gender')}
                      {!genderRequired && <> <span style={optionalStyle}>{t('assign.field.optionalSuffix')}</span></>}
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {genderOptions(config.genderPolicy).map((g) => (
                        <button
                          key={g}
                          data-testid="assign-kid-gender"
                          data-gender={g}
                          aria-pressed={draft.kidGender === g}
                          onClick={() => set({ kidGender: g })}
                          style={{
                            borderRadius: 20, padding: '0 15px', minHeight: 44, fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            ...(draft.kidGender === g
                              ? { background: 'var(--tru-status-teal)', color: 'var(--tru-accent-ink)', border: '1px solid var(--tru-status-teal)' }
                              : { background: 'var(--tru-surface-1)', color: '#c3c9c5', border: '1px solid var(--tru-border-14)' }),
                          }}
                        >{g}</button>
                      ))}
                    </div>
                    {/* Inline WHY while the restricted-ticket requirement is
                        unmet (quality P0): the same restriction-specific copy
                        the block bar uses — one message, two surfaces. */}
                    {genderRestriction && !draft.kidGender && (
                      <div data-testid="assign-gender-required" role="note" style={hintStyle('bad')}>
                        {t(genderRestriction === 'female'
                          ? (flags.noun === 'teenager' ? 'assign.block.genderMissing.teen.female' : 'assign.block.genderMissing.child.female')
                          : (flags.noun === 'teenager' ? 'assign.block.genderMissing.teen.male' : 'assign.block.genderMissing.child.male'))}
                      </div>
                    )}
                  </>
                )}
                {pool.length > 0 && (
                  <>
                    <div style={{ height: 14 }} />
                    <button
                      data-testid="assign-kid-back"
                      onClick={() => set({ addKid: false, kidFirst: '', kidLast: '', kidDobD: '', kidDobM: '', kidDobY: '', kidGender: '' })}
                      style={{ border: 0, background: 'transparent', color: 'var(--tru-text-tertiary)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0 }}
                    >{backChev} {t(flags.noun === 'teenager' ? 'assign.addChild.switchTeen' : 'assign.addChild.switchChild')}</button>
                  </>
                )}
              </>
            )}

            {/* Teen view-only access — the CHOSEN teen's own card; a valid
                address IS the opt-in (no separate switch). */}
            {teenCard && (
              <div data-testid="assign-teen-card" style={{ marginBlockStart: 18, borderRadius: 14, background: 'rgba(255,255,255,.035)', border: '1px solid rgba(255,255,255,.09)', padding: '14px 15px' }}>
                <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '-0.01em', marginBlockEnd: 5 }}>
                  {chosenTeen ? t('assign.teen.title', { name: chosenTeen }) : t('assign.teen.titleUnnamed')}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--tru-text-tertiary)', lineHeight: 1.55, marginBlockEnd: 12 }}>
                  {chosenTeen ? t('assign.teen.body', { name: chosenTeen }) : t('assign.teen.bodyUnnamed')}
                </div>
                <label style={lblStyle}>
                  {t('assign.teen.label')} <span style={optionalStyle}>{t('assign.field.optionalSuffix')}</span>
                </label>
                <input
                  data-testid="assign-teen-email"
                  value={draft.teenEmail}
                  onChange={(e) => set({ teenEmail: e.target.value })}
                  placeholder={t('assign.teen.placeholder')}
                  style={inputStyle(teenTone)}
                />
                <div data-testid="assign-teen-hint" style={hintStyle(teenTone)}>
                  {teenTone === 'idle' ? t('assign.teen.hintIdle') : teenTone === 'ok' ? t('assign.field.email.ok') : t('assign.field.email.bad')}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---- CONFIRM ---- */}
        {!done && cur === 'confirm' && (
          <div id="assign.step.confirm">
            <div style={{ ...summaryCardStyle, marginBlockEnd: 14 }}>
              {rows.map((r) => (
                <div key={r.k} data-testid="assign-summary-row" data-k={r.k} style={summaryRowStyle}>
                  <span style={{ flex: 1, fontSize: 12.5, color: 'var(--tru-text-tertiary)' }}>{r.k}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'end', overflowWrap: 'anywhere' }}>{r.v}</span>
                </div>
              ))}
            </div>
            <div data-testid="assign-next-note" style={{ fontSize: 11.5, color: 'var(--tru-text-quaternary)', lineHeight: 1.5 }}>
              {nextNote(draft, pool, t, policy, teenSelf)}
            </div>
            {genderBlocked && (
              <div data-testid="assign-gender-block" role="alert" style={verdictStyle('bad')}>{selfGender.reason}</div>
            )}
            {submitError && !genderBlocked && (
              <div role="alert" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--tru-status-error)', marginBlockStart: 12 }}>{submitError}</div>
            )}
          </div>
        )}

        {/* ---- DONE — wrong-address escape (guestparent one-step send,
            BOARDROOM-2026-09-05-120305 item 5): back to the PREFILLED entry
            step; the re-send submit is then a reassign, riding the verified
            revoke-then-rotate seam (prior invite revoked first — the old
            link is dead server-side; claim 409s on non-invited state). ---- */}
        {done && outcome && (
          <div id="assign.done">
            <div style={{ textAlign: 'center', padding: '6px 0 4px' }}>
              <div aria-hidden style={{ width: 58, height: 58, borderRadius: '50%', background: 'rgba(18,196,172,.16)', color: 'var(--tru-status-teal-bright)', display: 'grid', placeItems: 'center', fontSize: 27, margin: '0 auto 14px' }}>✓</div>
              <h1 data-testid="assign-done-heading" style={{ ...h1Style, fontSize: 21, margin: '0 0 6px' }}>
                {doneHeadingWithFlags(outcome, sent, t)}
              </h1>
              <p data-testid="assign-done-sub" style={{ ...introStyle, margin: '0 0 18px' }}>{doneSub(outcome, sent, t)}</p>
            </div>
            <div style={summaryCardStyle}>
              {rows.map((r) => (
                <div key={r.k} data-testid="assign-summary-row" data-k={r.k} style={summaryRowStyle}>
                  <span style={{ flex: 1, fontSize: 12.5, color: 'var(--tru-text-tertiary)' }}>{r.k}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'end', overflowWrap: 'anywhere' }}>{r.v}</span>
                </div>
              ))}
            </div>
            {outcome.mode === 'guestparent' && (
              <button
                data-testid="assign-done-resend"
                onClick={changeResend}
                style={{ display: 'block', margin: '14px auto 0', border: 0, background: 'transparent', color: 'var(--tru-text-tertiary)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: '0 8px', textDecoration: 'underline', textUnderlineOffset: 3 }}
              >{t('assign.done.changeResend')}</button>
            )}
          </div>
        )}
      </div>

      {/* BLOCK BAR — directly above the footer, its own horizontal padding
          matching the footer's (QA 9.2), naming the SPECIFIC missing field. */}
      {showBlock && (
        <div data-testid="assign-block" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: `10px ${blockPadX}px`, background: 'var(--tru-status-amber-bg-10)', borderBlockStart: '1px solid rgba(230,193,90,.28)' }}>
          <span aria-hidden style={{ flex: 'none', color: 'var(--tru-status-amber)', fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--tru-status-amber)', fontWeight: 700 }}>
            {blockText(cur, draft, policy, config, pool, raceDay, ctx.ticketType, t)}
          </span>
        </div>
      )}

      {/* FOOT */}
      <div id="assign.foot" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: footPad, borderBlockStart: '1px solid var(--tru-hairline-07)', background: 'var(--tru-surface-1)' }}>
        {done ? (
          onGetReady && outcome?.mode === 'me' ? (
            <>
              <button data-testid="assign-done-close" onClick={onDone} style={backBtnStyle}>{t('flow.backToOrder')}</button>
              <button data-testid="assign-done-getready" onClick={onGetReady} style={ctaStyle(true)}>{t('flow.getReadyNow')}</button>
            </>
          ) : (
            <button data-testid="assign-done-close" onClick={onDone} style={{ ...ctaStyle(true), width: '100%' }}>{t('flow.backToOrder')}</button>
          )
        ) : choice ? (
          // Single-choice steps: Back + quiet coach line — NO Continue (QA 10.2).
          <>
            <button data-testid="assign-back" onClick={back} style={backBtnStyle}>{t('flow.back')}</button>
            <span data-testid="assign-tap-note" style={{ flex: 1, fontSize: 11.5, color: 'var(--tru-text-muted)', textAlign: 'end' }}>
              {t('flow.tapToContinue')}
            </span>
          </>
        ) : (
          <>
            <button data-testid="assign-back" onClick={back} style={backBtnStyle}>{t('flow.back')}</button>
            {isLast ? (
              <button
                data-testid="assign-submit"
                onClick={submit}
                aria-disabled={!ok || genderBlocked || submitState === 'submitting'}
                style={ctaStyle(ok && !genderBlocked && submitState !== 'submitting')}
              >
                {submitState === 'submitting' ? t('assign.cta.sending') : confirmLabel(draft, t)}
              </button>
            ) : (
              <button data-testid="assign-continue" onClick={goNext} aria-disabled={!ok} style={ctaStyle(ok)}>
                {t('flow.continue')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Live submit seam
// ---------------------------------------------------------------------------

/** Live submit: my-api is the allocation authority for EVERY route. Self →
 *  self-holder bootstrap; other adult AND guestparent (parent's email) →
 *  email invite; child / guestchild (the teen my-route rides mode 'child' +
 *  minor) → the child-holder contract (launch-gate slice — these confirms are
 *  now SERVER-BACKED, closing the old client-only TODO WIRE seam). A
 *  re-assign revokes the standing allocation first on every server route.
 *  The client store still carries the DISPLAY overlay (picker pools, Orders
 *  row sub-lines) via persistLocalOutcome — a presentation record over the
 *  server truth, no longer the only record. Teen view-only login issuance
 *  stays dark (// TODO WIRE — teenLoginEmailSent honestly false). Everything
 *  runs ONLY at explicit confirm. */
export async function liveSubmit(
  orderId: string, ticketId: string, o: AssignOutcome,
  opts: { unclaimedSiblingSeatNo?: number | null; tr?: TranslateFn } = {},
): Promise<Partial<SendFlags>> {
  const tr = opts.tr ?? ((k, p) => translate('en', k, p))
  const base = myApiBase()
  const session = loadSession()
  if (!base || !session) {
    // Customer-language copy (BOARDROOM-2026-09-01-193406) — this message
    // surfaces verbatim in the flow's error alert.
    throw new Error('Assigning is not connected in this build yet — nothing was sent.')
  }
  try {
    const isChild = o.mode === 'child' || o.mode === 'guestchild'
    // Child facts resolve BEFORE anything destructive (review P1-3b): the
    // outcome's own newChild, or the store record the picker chose (childKey)
    // — the SAME facts the in-flow band + gender gates already judged. A
    // stale childKey throws HERE, with the standing holder still intact.
    const rec = isChild ? (o.newChild ?? listChildren().find((k) => k.key === o.childKey) ?? null) : null
    if (isChild && !rec) {
      // Unreachable through the flow (a child route always picks or adds),
      // but never POST fabricated facts on a stale key — and never after a
      // committed revoke.
      throw new Error(tr('assign.error.failed'))
    }
    // REPLACE pre-check, CHILD axis (review P1-3a — the twin of the flow's
    // sameUnitEmails email pre-flight): a replace's revoke commits before the
    // server's duplicate 422 can arrive, so a child already holding a SIBLING
    // seat of this unit must block BEFORE the revoke — typed like the server's
    // own 422 so the flow lands it on the details step (P1-2 surface).
    // Fresh (non-replace) submits are NOT pre-blocked: their server 422 is
    // non-destructive and authoritative (this device's records can be stale
    // or partial — the server remains the backstop either way).
    if (isChild && o.reassign && childDuplicateOnUnit(o)) {
      throw new MyApiError(
        'duplicate participant on this unit (client pre-check — nothing sent)',
        422, 'duplicate_participant', 'duplicate_participant',
      )
    }
    // Revoke ONLY on an explicit replace (BOARDROOM-2026-08-17-210222): an
    // unassigned seat's submit NEVER revokes. Applies to EVERY server route,
    // child-holder included (parity with the adult flow).
    if (o.reassign) {
      try {
        await revokeAllocation(session.idToken, base, orderId, ticketId)
      } catch (e) {
        // ALREADY-EMPTY SEAT (review fix F1a): a 404 from the revoke leg
        // ("revoke failed (404)" per myApi check()) means there is NO live
        // allocation on this seat — someone/something already revoked it
        // (a parallel device, CS, or a resendArmed pass whose prior invite
        // was cleaned up server-side). The revoke-then-rotate invariant is
        // PRESERVED — there is nothing left to revoke — so PROCEED to
        // allocate rather than stranding the user on a dead "revoke failed"
        // error over an empty seat. Every other failure still aborts BEFORE
        // anything destructive-adjacent fires.
        if (!(e instanceof MyApiError && e.status === 404)) throw e
      }
    }
    if (isChild && rec) {
      // SERVER-BACKED child seat (launch-gate slice): POST the child-holder
      // contract.
      await createChildHolder(session.idToken, base, orderId, ticketId, {
        child: {
          firstName: rec.first.trim(),
          lastName: rec.last.trim(),
          // 'YYYY-MM-DD', zero-padded (wizardModel.dobIso — one composer,
          // never re-derived). '' (incomplete parts — unreachable while
          // childDob is Required) rides as an honest null; the server's 422
          // is then the reject surface, never a guessed date.
          dob: dobIso({ dobD: rec.dobD, dobM: rec.dobM, dobY: rec.dobY }) || null,
          // Wire vocabulary 'M'|'F'|null — honest exact-token mapping only
          // (assignModel.wireGender; self-described / unknown stays null).
          gender: wireGender(rec.gender),
        },
        guest: o.mode === 'guestchild',
      })
      // KEEP the client display overlay: picker-pool continuity (the child
      // stays offered on later seats) + the Orders row sub-lines still read
      // from the local record — now a presentation layer over server truth.
      // The seat's my-api ticket row id is stamped on the record so ticketId-
      // keyed surfaces (Get-ready cards) find it without index re-derivation.
      persistLocalOutcome(o, ticketId)
      bumpReadinessRefresh()
      // Teen view-only login issuance is still dark (// TODO WIRE) — never
      // claim a send that did not happen.
      return { inviteEmailSent: false, teenLoginEmailSent: false }
    }
    if (o.mode === 'me') {
      // TEEN SELF-HOLDER (flag on, selfAge answered): an under-18 answer saves
      // the holder's own DOB to their profile FIRST (my-api's race-day age
      // source is the ACCOUNT DOB — the guardian request needs it), then the
      // self-assign carries the declared bracket. Best-effort DOB save: the
      // bracket alone still stamps the row 'minor' server-side. Flag off ⇒ no
      // selfAge ⇒ the call below is byte-identical to before.
      if (o.selfAge) {
        if (o.selfAge.dob && session.accessToken) {
          try {
            await updateProfile(session.accessToken, { birthdate: o.selfAge.dob })
          } catch { /* the declared bracket still routes the guardian signature */ }
        }
        await ensureSelfHolder(session.idToken, base, ticketId, fetch, { bracket: o.selfAge.bracket })
      } else {
        await ensureSelfHolder(session.idToken, base, ticketId)
      }
      // STALE-RECORD CLEAR (PR#124 review P1-2): the seat is now the OWNER's
      // — any lingering child/guestparent record for this seat key would keep
      // rendering the child's name over the new holder (identity bleed) and
      // mis-route Edit into the child wizard.
      clearLocalAssignment(o.seatKey)
      bumpReadinessRefresh()
      return { inviteEmailSent: true }
    }
    // Email-invite routes — other-adult (their address) and guestparent (the
    // PARENT's address). my-api returns the honest send flag. The guestparent
    // route carries parent_sign (BOARDROOM-2026-09-02-150714 / comms dispatch
    // BOARDROOM-150801, producer PR#122): the server then emits the dedicated
    // parent_sign_request letter greeting the PARENT, naming the child.
    // BOTH calls now carry the ALREADY-COLLECTED under/over-18 declaration
    // (o.bracket — GATE 4 declaration parity, d20a BOARDROOM-2026-09-08-
    // 134617): the allocation API REQUIRES it on the invite leg, so the FE
    // and API ship the contract together. CACHE WINDOW (deliberate, no
    // server-side grace): an older cached bundle without the field 422s
    // declaration_required until refresh — the server's message says so and
    // rides the pass-through below.
    const res = o.mode === 'guestparent'
      ? await allocateTicket(
          session.idToken, base, orderId, ticketId, o.email, o.bracket, fetch,
          { parentName: o.parentName, childName: o.name },
        )
      : await allocateTicket(session.idToken, base, orderId, ticketId, o.email, o.bracket)
    if (o.mode === 'guestparent') {
      // Overlay record so the row can read `Parent invited for {child} —
      // waiting for them to complete it` (child name/mode are client state
      // the wire cannot carry — // TODO WIRE). ticketId stamped: the buyer's
      // Get-ready waiting card (item 5) reads the holder state by row id.
      persistLocalOutcome(o, ticketId)
    } else {
      // STALE-RECORD CLEAR (review P1-2): an other-ADULT invite replaces
      // whatever child/guestparent record this seat carried.
      clearLocalAssignment(o.seatKey)
    }
    bumpReadinessRefresh()
    return { inviteEmailSent: res.inviteEmailSent, teenLoginEmailSent: false }
  } catch (e) {
    // Duplicate participant in one unit (CMT-2026-08-29-026): rethrow the
    // typed 422 UNMAPPED so the flow can land it on the email field with the
    // server's own plain copy (reason + serverMessage survive).
    if (e instanceof MyApiError && e.reason === 'duplicate_participant') throw e
    // Child-holder eligibility 422s (age|gender|dob_required): surface the
    // SERVER's own plain copy — the server gate is the authority; the FE never
    // re-words its verdict. 'dob_required' is the GATE 2 banded-seat missing-
    // DOB refusal (BOARDROOM-2026-09-07-142718); 'declaration_required' is
    // the GATE 4 missing-declaration reject (d20a) — this shipped FE always
    // sends the declaration, so it only fires from an OLDER CACHED BUNDLE
    // (the documented cache window) and the server's honest copy tells the
    // user to refresh. Same pass-through as the other axes. No server
    // message ⇒ the generic mapping below.
    if (
      e instanceof MyApiError && e.status === 422
      && (e.reason === 'age' || e.reason === 'gender' || e.reason === 'dob_required' || e.reason === 'declaration_required') && e.serverMessage
    ) {
      throw new Error(e.serverMessage)
    }
    throw new Error(
      e instanceof MyApiError && e.code === 'already_allocated'
        // Never coach a revoke (BOARDROOM-2026-08-17-210222): direct to the
        // ticket's open place when one exists; otherwise to the deliberate
        // replace path.
        ? (opts.unclaimedSiblingSeatNo != null
            ? tr('assign.error.liveHolderSibling', { n: opts.unclaimedSiblingSeatNo })
            : tr('assign.error.liveHolder'))
        : e instanceof Error ? e.message : tr('assign.error.service'),
    )
  }
}

/** Record the client-side child + seat DISPLAY state for an outcome — the
 *  picker-pool continuity + Orders row overlay layer. Since the child-holder
 *  contract landed this is presentation state OVER the server truth (the
 *  guestparent overlay remains the one route whose child name/mode the wire
 *  cannot carry — // TODO WIRE). `ticketId` (child-identity slice) stamps the
 *  seat's my-api ticket ROW id on the record so ticketId-keyed surfaces
 *  (Get-ready per-holding cards, the buyer's waiting card) can find it.
 *  Exported for the unit tests that prove pool separation + row overlays. */
export function persistLocalOutcome(o: AssignOutcome, ticketId?: string): void {
  let childKey = o.childKey
  if (o.newChild) {
    childKey = addChild({
      first: o.newChild.first, last: o.newChild.last,
      dobD: o.newChild.dobD, dobM: o.newChild.dobM, dobY: o.newChild.dobY,
      gender: o.newChild.gender, guest: o.newChild.guest,
    }).key
  }
  if (o.mode === 'child' || o.mode === 'guestchild' || o.mode === 'guestparent') {
    recordLocalAssignment({
      seatKey: o.seatKey,
      mode: o.mode === 'child' && o.minor ? 'teen' : o.mode,
      name: o.childName || o.name,
      childKey,
      tType: o.tType,
      evName: o.evName,
      parentEmail: o.mode === 'guestparent' ? o.email : '',
      parentName: o.parentName,
      minor: o.minor || o.mode === 'guestchild' || o.mode === 'guestparent' ? true : false,
      teenLogin: o.teenLogin,
      teenEmail: o.teenEmail,
      ...(ticketId ? { ticketId } : {}),
    })
  }
}

/** REPLACE pre-check, CHILD axis (PR#114 review P1-3a): does a SIBLING seat of
 *  the target's unit already hold THIS child, per the local seat records?
 *  Unit siblings share the target's `{orderRef}|{ticketIndex}.` seat-key
 *  prefix (seatKeyFor). Identity: the picked record's childKey, or — for an
 *  add-new submit — an exact name + full-DOB match against a sibling's
 *  recorded child (the same facts the server's duplicate gate judges; never a
 *  fuzzy guess). HONEST LIMITATION: local records only cover seats assigned
 *  from THIS device — a cross-device sibling is invisible here and the
 *  server's 422 remains the authoritative backstop (same staleness posture as
 *  the adult sameUnitEmails set, which at least loads live — the child wire
 *  carries no sibling identities yet). Exported for the liveSubmit tests. */
export function childDuplicateOnUnit(o: AssignOutcome): boolean {
  const dot = o.seatKey.lastIndexOf('.')
  if (dot < 0) return false
  const unitPrefix = o.seatKey.slice(0, dot + 1) // `{orderRef}|{ticketIndex}.`
  const { assigned, children } = getAssignStoreSnapshot()
  const norm = (s: string) => s.trim().toLowerCase()
  for (const [seatKey, a] of Object.entries(assigned)) {
    if (seatKey === o.seatKey || !seatKey.startsWith(unitPrefix)) continue
    if (!a.childKey) continue // guestparent overlays carry no child record
    if (o.childKey && a.childKey === o.childKey) return true
    if (o.newChild) {
      const sib = children.find((c) => c.key === a.childKey)
      if (
        sib
        && norm(sib.first) === norm(o.newChild.first)
        && norm(sib.last) === norm(o.newChild.last)
        && !!sib.dobD && sib.dobD === o.newChild.dobD
        && sib.dobM === o.newChild.dobM && sib.dobY === o.newChild.dobY
      ) {
        return true
      }
    }
  }
  return false
}

// ---------------------------------------------------------------------------
// Live route container
// ---------------------------------------------------------------------------

/** Partition the LIVE holder emails of the loaded order's allocation rows
 *  around the target seat (CMT-2026-08-29-026 + adversarial-review P1):
 *  - `sameUnitEmails` — holders of SIBLING rows of the target's unit; feeds
 *    the client-side duplicate pre-check that must fire BEFORE a replace's
 *    destructive revoke (the server's 422 hard block is the backstop).
 *  - `sameEventEmails` — holders on OTHER units; feeds the non-blocking
 *    time-clash nudge (cross-unit repeats are ALLOWED).
 *  The target row itself is excluded (replacing a holder with themselves is
 *  the seam's idempotent/no-op concern, not a duplicate); revoked holders
 *  never count; a unit-less target has no siblings (all others are
 *  event-wide). An order is single-event, so order scope IS same-event scope
 *  for the loaded data. */
export function partitionHolderEmails(
  rows: ReadonlyArray<{
    ticketId: string
    ticketUnitId?: string | null
    holder: { state: string; allocatedEmail: string | null } | null
  }>,
  targetTicketId: string,
): { sameUnitEmails: Set<string>; sameEventEmails: Set<string> } {
  const targetUnit = rows.find((r) => r.ticketId === targetTicketId)?.ticketUnitId ?? null
  const sameUnitEmails = new Set<string>()
  const sameEventEmails = new Set<string>()
  for (const r of rows) {
    if (r.ticketId === targetTicketId) continue
    const email = r.holder && r.holder.state !== 'revoked' ? r.holder.allocatedEmail : null
    if (!email) continue
    if (targetUnit != null && r.ticketUnitId === targetUnit) sameUnitEmails.add(normalizeEmail(email))
    else sameEventEmails.add(normalizeEmail(email))
  }
  return { sameUnitEmails, sameEventEmails }
}

export function AssignEntry() {
  const { liveState, orders, sessionEmail, eventById, ticketsForOrder } = useData()
  const { t, dir } = useLocale()
  // Direction-encoding back glyph — swapped in code, never CSS-mirrored
  // (README §RTL).
  const entryChev = dir === 'rtl' ? '›' : '‹'
  const { orderRef = '', ticketId = '', seatNo: seatNoParam = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const order = orders.find((o) => o.ref === orderRef) ?? null
  const { state, tickets, allocations, refresh } = useOrderSeats(order)
  // POST-EVENT CLOSE (BOARDROOM-2026-08-25-044751): server-computed eventClosed
  // from the shared readiness subscription (optional — null outside the
  // provider reads as open). The FE never re-derives close state from dates.
  const liveGetReady = useGetReadyLiveOptional()

  // Account-holder Cognito gender for the self-assign eligibility guard.
  const [holderGender, setHolderGender] = useState<string | undefined>(undefined)
  // TEEN SELF-HOLDER: the account DOB (null = unknown) settles the selfAge step.
  const [holderDob, setHolderDob] = useState<string | null>(null)
  useEffect(() => {
    const session = loadSession()
    if (!session?.accessToken) return
    let cancelled = false
    getProfile(session.accessToken)
      .then((p) => {
        if (cancelled) return
        setHolderGender(p.gender)
        setHolderDob(p.birthdate ?? null)
      })
      .catch(() => { /* unknown gender ⇒ never blocks (conservative) */ })
    return () => { cancelled = true }
  }, [])

  if (liveState !== 'ready') {
    return <div id="assign.root"><LiveDataNotice liveState={liveState} /></div>
  }
  // Per-seat targeting (BOARDROOM-2026-08-17-210222): the route's :ticketId is
  // the SEAT's OWN my-api ticket id; older deep links may carry the unit
  // anchor id, so the lookup accepts either.
  const ticket = tickets.find((t) => t.id === ticketId || t.seats.some((s) => s.ticketId === ticketId)) ?? null
  const seatNo = Number(seatNoParam)
  const seat = ticket
    ? ticket.seats.find((s) => s.seatNo === seatNo && (s.ticketId ?? ticket.id) === ticketId)
      ?? ticket.seats.find((s) => s.ticketId === ticketId)
      ?? ticket.seats.find((s) => s.seatNo === seatNo)
      ?? null
    : null
  const targetTicketId = seat?.ticketId ?? (ticket && ticket.seats.length === 1 ? ticket.id : null)
  const backPath = `/orders/${orderRef}`

  // Readiness eventClosed ORed with the orders-wire past verdict: readiness
  // only carries events where the caller HOLDS a ticket, so a purchaser whose
  // seats are ALL unassigned (the Duncan regression fixture) has no readiness
  // row — the orders-wire eventEndsAt closes that deep-link hole (review P1,
  // PR#100).
  const eventClosed = (order
    ? liveGetReady?.events.find((e) => e.id === order.eventId)?.eventClosed === true
    : false) || isPastEvent(allocations?.eventEndsAt)
  if (eventClosed) {
    return (
      <div id="assign.root" style={{ maxWidth: 'var(--content-max, 700px)' }}>
        <p role="status" data-testid="assign-event-ended" className="secondary" style={{ fontSize: 13 }}>
          {t('assign.entry.eventEnded')}
        </p>
        <button
          onClick={() => navigate(backPath)}
          style={{ border: 0, background: 'transparent', color: 'var(--tru-text-tertiary)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0 }}
        >{`${entryChev} ${t('flow.backToOrder')}`}</button>
      </div>
    )
  }

  if (!order || !ticket || !seat || state !== 'ready' || !targetTicketId) {
    return (
      <div id="assign.root" style={{ maxWidth: 'var(--content-max, 700px)' }}>
        <p role="status" className="secondary" style={{ fontSize: 13 }}>
          {state === 'loading'
            ? t('assign.entry.loading')
            : order && ticket && seat && state === 'ready' && !targetTicketId
              ? t('assign.entry.noSeat')
              : t('assign.entry.cantOpen')}
        </p>
        <button
          onClick={() => navigate(backPath)}
          style={{ border: 0, background: 'transparent', color: 'var(--tru-text-tertiary)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0 }}
        >{`${entryChev} ${t('flow.backToOrder')}`}</button>
      </div>
    )
  }

  const routeName = (location.state as { accountName?: string } | null)?.accountName
  // Open sibling place on the same ticket while THIS seat is held: the 409
  // error copy directs there rather than coaching a replace.
  const unassignedSiblingSeatNo = seat.status !== 'unassigned'
    ? ticket.seats.find((s) => s.status === 'unassigned' && s.seatNo !== seat.seatNo && !!s.ticketId)?.seatNo ?? null
    : null
  const ev = eventById(order.eventId)
  // Seat identity: `{orderRef}|{ticketIndex}.{seatIndex}` — the orderRef is
  // part of the key (CHANGELOG 1.10). Indices are 0-based positions within
  // the ORDER's box-office ticket list.
  const orderTickets = ticketsForOrder(order.id)
  const ticketIndex = Math.max(0, orderTickets.findIndex((x) => x.id === ticket.id))
  const seatKey = seatKeyFor(order.ref, ticketIndex, seat.seatNo - 1)

  // Holder-email partition (CMT-2026-08-29-026 + adversarial-review P1), from
  // the ALREADY LOADED order allocations — no new endpoint: same-UNIT sibling
  // holders drive the pre-revoke duplicate check; other-unit holders drive
  // the non-blocking time-clash nudge.
  const { sameUnitEmails, sameEventEmails } = partitionHolderEmails(
    allocations?.tickets ?? [], targetTicketId,
  )

  const ctx: AssignCtx = {
    orderRef: order.ref,
    ticketIndex,
    nTickets: orderTickets.length || 1,
    ticketType: ticket.typeName,
    evName: ev?.name ?? '',
    seatNo: seat.seatNo,
    of: ticket.seats.length,
    holder: seat.status !== 'unassigned' ? holderLabel(seat) : '',
    myName: routeName || sessionEmail || 'your account',
    myEmail: sessionEmail ?? '',
    myGender: holderGender,
  }

  const raceDay = raceDayFrom(allocations?.eventStartsAt)
  return (
    <AssignFlow
      ctx={ctx}
      seatKey={seatKey}
      teenSelf={teenSelfHolderEnabled() ? { enabled: true, profileAgeOnRace: ageOnRaceFromIso(holderDob, raceDay) } : undefined}
      // Band from the box-office R1 eligibility contract when present; an
      // ABSENT band ASKS the age bracket on the someone-else route (safe
      // default — never silently-adult; assignModel asksAgeBracket).
      policy={resolveTicketPolicy(ticket.eligibility)}
      // Real race-day anchor off the my-api orders wire (allocations
      // eventStartsAt — BOARDROOM-2026-09-01-175306 item 1; closes the P3
      // flagged in MY-2026-09-01-154049: band checks at band edges are now
      // correct). HONEST FALLBACK: an older my-api / degraded read carries no
      // date, and raceDayFrom then anchors to "now" — the old approximation,
      // kept only where no real date exists.
      raceDay={raceDay}
      sameEventEmails={sameEventEmails}
      sameUnitEmails={sameUnitEmails}
      onSubmit={(o) => liveSubmit(order.id, targetTicketId, o, { unclaimedSiblingSeatNo: unassignedSiblingSeatNo, tr: t })}
      onExit={() => navigate(backPath)}
      onDone={() => {
        refresh()
        navigate(backPath)
      }}
      onGetReady={() => navigate('/get-ready')}
    />
  )
}
